import {
  defaultFairnessCopy,
  FAIRNESS_EXPLANATION,
  fairnessVerdict,
  fairnessVerifyFacts,
} from "@loot-os/api-contract";
import type { FairnessVerifyContent } from "@loot-os/design-system/templates/fairness/fairnessContent";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { track } from "../../../../lib/analytics.js";
import { verificationQueryOptions } from "../../../../lib/queries.js";
import { useTerminology } from "../../../../lib/terminology.js";

/**
 * Verifying one open: `GET /v1/opens/:id/verification`, recomputed in the browser through
 * `@loot-os/client/verify`. `?openId=` (the account page's history links here) verifies on
 * arrival; submitting the same id again re-verifies rather than serving the cache.
 */
export function useFairnessVerifyContent(): FairnessVerifyContent {
  const terms = useTerminology();
  const copy = defaultFairnessCopy().verify;
  const { openId: linkedOpenId } = useSearch({ from: "/fairness" });
  const [openId, setOpenId] = useState(linkedOpenId ?? "");
  const [submittedId, setSubmittedId] = useState(linkedOpenId ?? "");

  // Auto-verify when arriving via a "Verify" link from the open history.
  useEffect(() => {
    if (linkedOpenId) {
      setOpenId(linkedOpenId);
      setSubmittedId(linkedOpenId);
    }
  }, [linkedOpenId]);

  const query = useQuery({
    ...verificationQueryOptions(submittedId || "none"),
    enabled: !!submittedId,
  });
  const busy = query.isFetching;
  const bundle = query.data?.bundle ?? null;
  const result = query.data?.result ?? null;
  const error =
    !busy && query.error
      ? query.error instanceof Error
        ? query.error.message
        : "verification failed"
      : null;

  // One event per verification answered: keyed on the fetch that produced it, so a re-render
  // (or StrictMode's doubled effect) never records the same answer twice.
  const reportedRef = useRef<string | null>(null);
  const answeredAt =
    query.errorUpdatedAt > query.dataUpdatedAt ? query.errorUpdatedAt : query.dataUpdatedAt;
  useEffect(() => {
    if (busy || !submittedId || !answeredAt) return;
    const key = `${submittedId}:${answeredAt}`;
    if (reportedRef.current === key) return;
    reportedRef.current = key;
    track("fairness_verified", {
      outcome: query.error ? "error" : result?.valid ? "valid" : "invalid",
    });
  }, [busy, submittedId, answeredAt, query.error, result]);

  return {
    title: copy.title,
    description: FAIRNESS_EXPLANATION.verify,
    openId: { ...copy.openId, value: openId, onChange: setOpenId },
    submit: { ...copy.submit, busy, disabled: busy || !openId.trim() },
    onSubmit: () => {
      const id = openId.trim();
      if (!id) return;
      if (id === submittedId) void query.refetch();
      else setSubmittedId(id);
    },
    error,
    result:
      bundle && result
        ? { verdict: fairnessVerdict(result), facts: fairnessVerifyFacts(terms, bundle, result) }
        : null,
  };
}
